import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import ReactFlow, {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  MarkerType,
  getStraightPath,
  MiniMap,
  type Edge,
  type EdgeProps,
  type Node,
  type OnEdgesChange,
  type OnNodesChange,
  useReactFlow,
  useNodesInitialized,
  useStore,
  useEdgesState,
  useNodesState
} from 'reactflow';
import 'reactflow/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';

import type { TopologyV2Edgeschema, TopologyV2Graphschema, TopologyV2Nodeschema } from '@app/types/sdk';
import { ActivityIcon, PinIcon } from '@app/components/icons';
import {
  formatTopologyPresentationValue,
  topologyPropertyRows,
  topologyPresentationProps,
  topologyPresentationString
} from './presentation';
import { inferTopologyPolicyNodeSummary } from '@app/pages/org/topology/policyPresentation';
import {
  absoluteNodeOrigin,
  buildEdgeChipNodes,
  diagramNodeDimensions,
  isEdgeChipRender,
  pickSideByBoxes,
  topAncestorByNodeId,
  type DeviceBox,
  type EdgeChipNodeData,
  type PortSide
} from './topologyEdgeChips';

type TopologyNode = TopologyV2Nodeschema;
type TopologyEdge = TopologyV2Edgeschema;
type TopologyGraph = TopologyV2Graphschema;
export type TopologyHoverPropertyRow = { key: string; label: string; value: string };

export const topologyHoverPropertyRows = (
  properties: Record<string, unknown> | undefined,
  runtimeRows: TopologyHoverPropertyRow[] = [],
  maxRows = 14
): { rows: TopologyHoverPropertyRow[]; truncated: boolean } => {
  const safeRuntimeRows = runtimeRows.slice(0, maxRows);
  const valueRows = topologyPropertyRows(properties)
    .filter((entry) => !entry.group)
    .map((entry) => ({ key: entry.key, label: entry.label, value: entry.value }));
  const remaining = Math.max(0, maxRows - safeRuntimeRows.length);
  return {
    rows: [...safeRuntimeRows, ...valueRows.slice(0, remaining)],
    truncated: runtimeRows.length > safeRuntimeRows.length || valueRows.length > remaining
  };
};

type L3DeviceInterfaceView = {
  id: string;
  label: string;
  adminState: string;
  operState: string;
  stateTone: L3InterfaceStateTone;
  properties: Record<string, unknown>;
};
type L3DeviceRouteTableActionView = {
  label: string;
  kind: string;
  method: string;
  href: string;
  timeMode?: string;
  presentation?: string;
  deviceId?: string;
  vrf?: string;
  enabled: boolean;
  reasonUnavailable?: string;
};
type L3DeviceRouteTableSummary = {
  defaultRouteCount?: number;
  connectedCount?: number;
  hostCount?: number;
  staticCount?: number;
  dynamicCount?: number;
  ecmpPrefixCount?: number;
};
type L3DeviceRouteTableRow = {
  rowId: string;
  vrf: string;
  prefix: string;
  nextHop?: string | null;
  resolvedEgressInterface?: string | null;
  protocol: string;
  metric?: number | null;
  preference?: number | null;
};
type L3DeviceRouteTablePage = {
  deviceId: string;
  siteId: string;
  vrf: string;
  snapshotId: string;
  tableType: string;
  commandId: string;
  vendor: string;
  os: string;
  timeMode: string;
  asOf?: string | null;
  collectedAt: string;
  dataState: string;
  attributionMethod: string;
  rowCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  rowsTruncated: boolean;
  warningCount: number;
  warnings?: string[];
  summary?: L3DeviceRouteTableSummary | null;
  items: L3DeviceRouteTableRow[];
};
type L3DeviceRouteTableState = {
  requestKey: string;
  status: 'idle' | 'loading' | 'ready' | 'error';
  data?: L3DeviceRouteTablePage;
  error?: string;
};
type L3DeviceVrfView = {
  id: string;
  label: string;
  interfaces: L3DeviceInterfaceView[];
  routeTable?: L3DeviceRouteTableActionView;
};
type L3InterfaceStateTone = 'healthy' | 'down' | 'warning' | 'unknown';

const ROUTE_TABLE_ACTION_ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:/i;
const ROUTE_TABLE_ACTION_PATH_PATTERN = /^\/?orgs\/[^/]+\/devices\/[^/]+\/route-table(?:\?.*)?$/i;

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const getString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);
const getTopologyPropertyValue = (properties: Record<string, unknown> | undefined, key: string): unknown => {
  if (!properties) {
    return undefined;
  }
  const direct = properties[key];
  if (direct !== undefined) {
    return direct;
  }
  const parts = key
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) {
    return undefined;
  }
  let current: unknown = properties;
  for (const part of parts) {
    const record = asRecord(current);
    if (!record) {
      return undefined;
    }
    current = record[part];
    if (current === undefined) {
      return undefined;
    }
  }
  return current;
};
export const getTopologyPropertyString = (
  properties: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const value = getTopologyPropertyValue(properties, key);
  return typeof value === 'string' ? value : undefined;
};
const getNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};
const getStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const items = value.filter((item): item is string => typeof item === 'string');
  return items.length > 0 ? items : undefined;
};

const getBoolean = (value: unknown): boolean =>
  value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true');

const normalizedString = (value: unknown): string => (typeof value === 'string' ? value.trim().toLowerCase() : '');
const nodeLevel = (node?: TopologyNode): string => normalizedString(node?.labels?.level);
const nodeDomain = (node?: TopologyNode): string => normalizedString(node?.labels?.domain);
const nodeInstance = (node?: TopologyNode): string => normalizedString(node?.labels?.instance);
const isDeviceNode = (node?: TopologyNode): boolean => nodeLevel(node) === 'device';
const isL3MembershipGraph = (graph?: TopologyGraph | null): boolean => {
  const meta = asRecord(graph?.meta);
  const context = asRecord(meta?.context);
  return normalizedString(context?.purpose ?? meta?.purpose) === 'l3_membership';
};
const isL3VrfNode = (node?: TopologyNode): boolean =>
  nodeLevel(node) === 'domain' && (nodeDomain(node) === 'routing' || nodeInstance(node).startsWith('vrf:'));
const isL3InterfaceNode = (node?: TopologyNode): boolean => nodeLevel(node) === 'interface';
const isL3SubnetNode = (node?: TopologyNode): boolean => nodeLevel(node) === 'domain' && nodeDomain(node) === 'ip';
export const normalizeInterfaceStateLabel = (value: unknown): string => {
  const normalized = normalizedString(value);
  switch (normalized) {
    case 'up':
    case 'down':
    case 'testing':
    case 'unknown':
      return normalized;
    default:
      return normalized ? 'unknown' : '';
  }
};

export const l3InterfaceStateTone = (adminState: unknown, operState: unknown): L3InterfaceStateTone => {
  const admin = normalizeInterfaceStateLabel(adminState);
  const oper = normalizeInterfaceStateLabel(operState);
  if (admin === 'up' && oper === 'up') {
    return 'healthy';
  }
  if (admin === 'down' || oper === 'down') {
    return 'down';
  }
  if (admin === 'testing' || oper === 'testing') {
    return 'warning';
  }
  if ((admin === '' || admin === 'unknown') && (oper === '' || oper === 'unknown')) {
    return 'unknown';
  }
  if (admin || oper) {
    return 'warning';
  }
  return 'unknown';
};

const l3InterfaceChipStyle = (tone: L3InterfaceStateTone): CSSProperties => {
  switch (tone) {
    case 'healthy':
      return {
        ['--l3-intf-chip-border' as string]: 'rgba(22, 163, 74, 0.9)',
        ['--l3-intf-chip-border-hover' as string]: 'rgba(21, 128, 61, 1)',
        ['--l3-intf-chip-ring' as string]: 'rgba(22, 163, 74, 0.24)',
        ['--l3-intf-chip-shadow' as string]: '0 10px 22px rgba(22, 163, 74, 0.2)',
        ['--l3-intf-chip-background' as string]: 'linear-gradient(180deg, rgba(240, 253, 244, 0.98), rgba(220, 252, 231, 0.96))',
        ['--l3-intf-chip-text' as string]: '#166534'
      };
    case 'down':
      return {
        ['--l3-intf-chip-border' as string]: 'rgba(220, 38, 38, 0.9)',
        ['--l3-intf-chip-border-hover' as string]: 'rgba(185, 28, 28, 1)',
        ['--l3-intf-chip-ring' as string]: 'rgba(220, 38, 38, 0.24)',
        ['--l3-intf-chip-shadow' as string]: '0 10px 22px rgba(220, 38, 38, 0.2)',
        ['--l3-intf-chip-background' as string]: 'linear-gradient(180deg, rgba(254, 242, 242, 0.98), rgba(254, 226, 226, 0.96))',
        ['--l3-intf-chip-text' as string]: '#991b1b'
      };
    case 'warning':
      return {
        ['--l3-intf-chip-border' as string]: 'rgba(217, 119, 6, 0.9)',
        ['--l3-intf-chip-border-hover' as string]: 'rgba(180, 83, 9, 1)',
        ['--l3-intf-chip-ring' as string]: 'rgba(217, 119, 6, 0.24)',
        ['--l3-intf-chip-shadow' as string]: '0 10px 22px rgba(217, 119, 6, 0.18)',
        ['--l3-intf-chip-background' as string]: 'linear-gradient(180deg, rgba(255, 251, 235, 0.98), rgba(254, 243, 199, 0.96))',
        ['--l3-intf-chip-text' as string]: '#92400e'
      };
    default:
      return {
        ['--l3-intf-chip-border' as string]: 'rgba(100, 116, 139, 0.8)',
        ['--l3-intf-chip-border-hover' as string]: 'rgba(71, 85, 105, 0.92)',
        ['--l3-intf-chip-ring' as string]: 'rgba(148, 163, 184, 0.22)',
        ['--l3-intf-chip-shadow' as string]: '0 10px 22px rgba(100, 116, 139, 0.14)',
        ['--l3-intf-chip-background' as string]: 'linear-gradient(180deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.96))',
        ['--l3-intf-chip-text' as string]: '#334155'
      };
  }
};
export const isSafeRouteTableActionHref = (href: string): boolean => {
  const trimmed = href.trim();
  if (!trimmed || trimmed.startsWith('//') || ROUTE_TABLE_ACTION_ABSOLUTE_URL_PATTERN.test(trimmed)) {
    return false;
  }
  return ROUTE_TABLE_ACTION_PATH_PATTERN.test(trimmed);
};
export const routeTableActionFromProperties = (properties?: Record<string, unknown>): L3DeviceRouteTableActionView | undefined => {
  const routeTable = asRecord(asRecord(asRecord(properties?.ui)?.actions)?.routeTable);
  if (!routeTable) {
    return undefined;
  }
  const href = getString(routeTable.href);
  if (!href) {
    return undefined;
  }
  const kind = getString(routeTable.kind) ?? 'device_api';
  const method = (getString(routeTable.method) ?? 'GET').trim().toUpperCase();
  const presentation = getString(routeTable.presentation);
  const payloadEnabled = routeTable.enabled === false ? false : true;
  const hrefAllowed = isSafeRouteTableActionHref(href);
  const enabled = payloadEnabled && kind === 'device_api' && method === 'GET' && hrefAllowed;
  let reasonUnavailable = getString(routeTable.reasonUnavailable);
  if (!reasonUnavailable && !hrefAllowed) {
    reasonUnavailable = 'Route table action URL is invalid.';
  } else if (!reasonUnavailable && method !== 'GET') {
    reasonUnavailable = 'Route table action method is unsupported.';
  } else if (!reasonUnavailable && kind !== 'device_api') {
    reasonUnavailable = 'Route table action kind is unsupported.';
  }
  return {
    label: getString(routeTable.label) ?? 'Route table',
    kind,
    method: 'GET',
    href,
    timeMode: getString(routeTable.timeMode),
    presentation,
    deviceId: getString(routeTable.deviceId),
    vrf: getString(routeTable.vrf),
    enabled,
    reasonUnavailable
  };
};

const normalizeRouteTableSummary = (value: unknown): L3DeviceRouteTableSummary | null => {
  const summary = asRecord(value);
  if (!summary) {
    return null;
  }
  return {
    defaultRouteCount: getNumber(summary.defaultRouteCount),
    connectedCount: getNumber(summary.connectedCount),
    hostCount: getNumber(summary.hostCount),
    staticCount: getNumber(summary.staticCount),
    dynamicCount: getNumber(summary.dynamicCount),
    ecmpPrefixCount: getNumber(summary.ecmpPrefixCount)
  };
};

const normalizeRouteTableRow = (value: unknown, index: number, fallbackVrf: string): L3DeviceRouteTableRow | null => {
  const row = asRecord(value);
  if (!row) {
    return null;
  }
  const prefix = getString(row.prefix);
  if (!prefix) {
    return null;
  }
  const protocol = getString(row.protocol) ?? 'unknown';
  const vrf = getString(row.vrf) ?? fallbackVrf;
  const nextHop = getString(row.nextHop);
  const resolvedEgressInterface = getString(row.resolvedEgressInterface);
  return {
    rowId: getString(row.rowId) ?? `${vrf || 'unknown'}|${prefix}|${nextHop ?? '-'}|${resolvedEgressInterface ?? '-'}|${protocol}|${index}`,
    vrf,
    prefix,
    nextHop: nextHop ?? null,
    resolvedEgressInterface: resolvedEgressInterface ?? null,
    protocol,
    metric: getNumber(row.metric) ?? null,
    preference: getNumber(row.preference) ?? null
  };
};

export const normalizeRouteTablePage = (value: unknown): L3DeviceRouteTablePage => {
  const page = asRecord(value);
  const vrf = getString(page?.vrf) ?? '';
  const items = Array.isArray(page?.items)
    ? page.items
        .map((row, index) => normalizeRouteTableRow(row, index, vrf))
        .filter((row): row is L3DeviceRouteTableRow => row !== null)
    : [];
  return {
    deviceId: getString(page?.deviceId) ?? '',
    siteId: getString(page?.siteId) ?? '',
    vrf,
    snapshotId: getString(page?.snapshotId) ?? '',
    tableType: getString(page?.tableType) ?? '',
    commandId: getString(page?.commandId) ?? '',
    vendor: getString(page?.vendor) ?? '',
    os: getString(page?.os) ?? '',
    timeMode: getString(page?.timeMode) ?? '',
    asOf: getString(page?.asOf) ?? null,
    collectedAt: getString(page?.collectedAt) ?? '',
    dataState: getString(page?.dataState) ?? '',
    attributionMethod: getString(page?.attributionMethod) ?? '',
    rowCount: getNumber(page?.rowCount) ?? items.length,
    limit: getNumber(page?.limit) ?? items.length,
    offset: getNumber(page?.offset) ?? 0,
    hasMore: getBoolean(page?.hasMore),
    rowsTruncated: getBoolean(page?.rowsTruncated),
    warningCount: getNumber(page?.warningCount) ?? 0,
    warnings: getStringArray(page?.warnings),
    summary: normalizeRouteTableSummary(page?.summary),
    items
  };
};

const overlayUI = (properties?: Record<string, unknown>): Record<string, unknown> | undefined => asRecord(properties?.ui);

type RuntimeTone = 'info' | 'normal' | 'success' | 'warning' | 'critical';
type RuntimeOverlay = {
  id: string;
  label: string;
  value: unknown;
  unit?: string;
  effects: string[];
  tone: RuntimeTone;
  deliveryMode?: 'live' | 'fixed';
  updatedAt?: string;
};
type RuntimeBadgeModel = {
  text: string;
  label?: string;
  value?: string;
  tone: RuntimeTone;
  deliveryMode?: RuntimeOverlay['deliveryMode'];
};
type ProviderEdgePortBadgeRecord = {
  interfaceName?: string;
  classification?: string;
};

const runtimeToneOrder: Record<RuntimeTone, number> = {
  normal: 0,
  info: 1,
  success: 2,
  warning: 3,
  critical: 4
};

const runtimeToneStyles: Record<RuntimeTone, { stroke: string; fill: string; text: string; shadow: string }> = {
  normal: { stroke: '#64748b', fill: 'rgba(100, 116, 139, 0.12)', text: '#334155', shadow: 'rgba(100, 116, 139, 0.18)' },
  info: { stroke: '#2563eb', fill: 'rgba(219, 234, 254, 0.82)', text: '#1e3a8a', shadow: 'rgba(37, 99, 235, 0.22)' },
  success: { stroke: '#16a34a', fill: 'rgba(220, 252, 231, 0.86)', text: '#166534', shadow: 'rgba(22, 163, 74, 0.2)' },
  warning: { stroke: '#d97706', fill: 'rgba(254, 243, 199, 0.88)', text: '#92400e', shadow: 'rgba(217, 119, 6, 0.22)' },
  critical: { stroke: '#dc2626', fill: 'rgba(254, 226, 226, 0.86)', text: '#991b1b', shadow: 'rgba(220, 38, 38, 0.24)' }
};

const normalizeRuntimeTone = (value: unknown): RuntimeTone => {
  const tone = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (tone === 'info' || tone === 'success' || tone === 'warning' || tone === 'critical') {
    return tone;
  }
  return 'normal';
};

const normalizeRuntimeDeliveryMode = (value: unknown): 'live' | 'fixed' | undefined => {
  const delivery = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (delivery === 'live') {
    return 'live';
  }
  if (delivery === 'snapshot' || delivery === 'fixed') {
    return 'fixed';
  }
  return undefined;
};

const runtimeOverlays = (properties?: Record<string, unknown>): RuntimeOverlay[] => {
  const overlays = asRecord(overlayUI(properties)?.runtimeOverlays);
  if (!overlays) {
    return [];
  }
  const out: RuntimeOverlay[] = [];
  Object.entries(overlays).forEach(([id, raw]) => {
    const rec = asRecord(raw);
    if (!rec) {
      return;
    }
    const effects = Array.isArray(rec.effects)
      ? rec.effects.map((effect) => (typeof effect === 'string' ? effect.trim() : '')).filter(Boolean)
      : [];
    const unit = getString(rec.unit);
    const updatedAt = getString(rec.updatedAt);
    out.push({
      id,
      label: getString(rec.label) ?? id,
      value: rec.value,
      effects,
      tone: normalizeRuntimeTone(rec.tone),
      ...(normalizeRuntimeDeliveryMode(rec.deliveryMode) ? { deliveryMode: normalizeRuntimeDeliveryMode(rec.deliveryMode) } : {}),
      ...(unit ? { unit } : {}),
      ...(updatedAt ? { updatedAt } : {})
    });
  });
  return out;
};

const runtimeOverlaysWithEffect = (properties: Record<string, unknown> | undefined, effects: string[]): RuntimeOverlay[] => {
  const allowed = new Set(effects);
  return runtimeOverlays(properties).filter((overlay) => overlay.effects.some((effect) => allowed.has(effect)));
};

const strongestRuntimeTone = (overlays: RuntimeOverlay[]): RuntimeTone => {
  return overlays.reduce<RuntimeTone>((best, overlay) => {
    return runtimeToneOrder[overlay.tone] > runtimeToneOrder[best] ? overlay.tone : best;
  }, 'normal');
};

const formatRuntimeValue = (overlay: RuntimeOverlay): string => {
  let value = formatTopologyPresentationValue(overlay.value);
  if (typeof overlay.value === 'number' && Number.isFinite(overlay.value)) {
    value =
      Math.abs(overlay.value) >= 100 || Number.isInteger(overlay.value)
        ? String(Math.round(overlay.value))
        : overlay.value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  }
  const unit = overlay.unit?.trim();
  if (!unit || value.endsWith(unit)) {
    return value;
  }
  return unit === '%' ? `${value}%` : `${value} ${unit}`;
};

const runtimeBadgeModel = (overlay: RuntimeOverlay): RuntimeBadgeModel => {
  const value = formatRuntimeValue(overlay);
  const label = overlay.label.trim();
  if (label && label !== overlay.id) {
    return {
      text: `${label}: ${value}`,
      label,
      value,
      tone: overlay.tone,
      deliveryMode: overlay.deliveryMode
    };
  }
  return {
    text: value,
    tone: overlay.tone,
    deliveryMode: overlay.deliveryMode
  };
};

const runtimeDeliveryRank = (deliveryMode: RuntimeOverlay['deliveryMode']): number => {
  switch (deliveryMode) {
    case 'live':
      return 0;
    case 'fixed':
      return 1;
    default:
      return 2;
  }
};

const runtimeDeliveryIcon = (deliveryMode: RuntimeOverlay['deliveryMode']) => {
  switch (deliveryMode) {
    case 'live':
      return <ActivityIcon size={10} className="topology-v2__runtime-badge-delivery-icon" aria-hidden="true" />;
    case 'fixed':
      return <PinIcon size={10} className="topology-v2__runtime-badge-delivery-icon" aria-hidden="true" />;
    default:
      return null;
  }
};

const runtimeLabelText = (properties: Record<string, unknown> | undefined, effects: string[]): string => {
  return runtimeOverlaysWithEffect(properties, effects).map(formatRuntimeValue).filter(Boolean).join(' / ');
};

const L3_DEVICE_VRF_COLUMNS = 2;
const L3_DEVICE_HEADER_HEIGHT = 42;
const L3_DEVICE_VRF_PILL_HEIGHT = 30;
const L3_DEVICE_VRF_PILL_GAP = 8;
const L3_DEVICE_FOOTER_PADDING = 18;
const L3_SUBNET_NODE_SIZE = 112;

const l3DeviceHeight = (vrfCount: number): number => {
  const rows = Math.max(1, Math.ceil(Math.max(0, vrfCount) / L3_DEVICE_VRF_COLUMNS));
  return Math.max(
    DEVICE_NODE_HEIGHT_COMPACT,
    L3_DEVICE_HEADER_HEIGHT +
      rows * L3_DEVICE_VRF_PILL_HEIGHT +
      Math.max(0, rows - 1) * L3_DEVICE_VRF_PILL_GAP +
      L3_DEVICE_FOOTER_PADDING
  );
};

const formatRouteTableNumber = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? String(value) : '-';

const routeTableActionRequestKey = (action?: L3DeviceRouteTableActionView): string =>
  action ? action.href : '';

const L3RouteTablePanel = ({
  action,
  state
}: {
  action: L3DeviceRouteTableActionView;
  state?: L3DeviceRouteTableState;
}) => {
  if (!action.enabled) {
    return <div className="topology-v2__l3-route-table-empty">{action.reasonUnavailable ?? 'Route table unavailable'}</div>;
  }
  if (!state || state.status === 'idle' || state.status === 'loading') {
    return <div className="topology-v2__l3-route-table-empty">Loading route table...</div>;
  }
  if (state.status === 'error') {
    return <div className="topology-v2__l3-route-table-empty">{state.error ?? 'Failed to load route table'}</div>;
  }
  const page = state.data;
  const items = Array.isArray(page?.items) ? page.items : [];
  if (!page || items.length === 0) {
    return <div className="topology-v2__l3-route-table-empty">No routes</div>;
  }
  const summary = page.summary ?? undefined;
  return (
    <div className="topology-v2__l3-route-table-panel">
      <div className="topology-v2__l3-route-table-meta">
        <span>{page.rowCount} routes</span>
        <span>{page.vendor || page.os || page.tableType}</span>
        {summary?.ecmpPrefixCount ? <span>{summary.ecmpPrefixCount} ECMP prefixes</span> : null}
      </div>
      <div className="topology-v2__l3-route-table-wrap">
        <table className="topology-v2__l3-route-table">
          <colgroup>
            <col className="topology-v2__l3-route-table-col topology-v2__l3-route-table-col--prefix" />
            <col className="topology-v2__l3-route-table-col topology-v2__l3-route-table-col--next-hop" />
            <col className="topology-v2__l3-route-table-col topology-v2__l3-route-table-col--interface" />
            <col className="topology-v2__l3-route-table-col topology-v2__l3-route-table-col--protocol" />
            <col className="topology-v2__l3-route-table-col topology-v2__l3-route-table-col--metric" />
            <col className="topology-v2__l3-route-table-col topology-v2__l3-route-table-col--preference" />
          </colgroup>
          <thead>
            <tr>
              <th>Prefix</th>
              <th>Next hop</th>
              <th>Interface</th>
              <th>Protocol</th>
              <th>Metric</th>
              <th>Preference</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.rowId}>
                <td>{row.prefix}</td>
                <td>{row.nextHop || '-'}</td>
                <td>{row.resolvedEgressInterface || '-'}</td>
                <td>{row.protocol || '-'}</td>
                <td>{formatRouteTableNumber(row.metric)}</td>
                <td>{formatRouteTableNumber(row.preference)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {page.hasMore && <div className="topology-v2__l3-route-table-empty">Showing {items.length} of {page.rowCount} routes</div>}
    </div>
  );
};

const L3DeviceNodeContent = ({
  label,
  vrfs,
  routeTableContextKey,
  requestTopologyAction
}: {
  label: string;
  vrfs: L3DeviceVrfView[];
  routeTableContextKey: string;
  requestTopologyAction?: (href: string, init?: RequestInit) => Promise<Response>;
}) => {
  const [activeVrfId, setActiveVrfId] = useState<string | null>(null);
  const [activeInterfaceId, setActiveInterfaceId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<'interfaces' | 'routeTable'>('interfaces');
  const [routeTableStateByVrfId, setRouteTableStateByVrfId] = useState<Record<string, L3DeviceRouteTableState>>({});
  const activeVrf = vrfs.find((vrf) => vrf.id === activeVrfId) ?? null;
  const activeInterface = activeVrf?.interfaces.find((intf) => intf.id === activeInterfaceId) ?? null;
  const activeRouteTableRequestKey = routeTableActionRequestKey(activeVrf?.routeTable);
  const activeRouteTableState = activeVrf
    ? routeTableStateByVrfId[activeVrf.id]?.requestKey === activeRouteTableRequestKey
      ? routeTableStateByVrfId[activeVrf.id]
      : undefined
    : undefined;

  useEffect(() => {
    setActiveInterfaceId(null);
    setActivePanel('interfaces');
  }, [activeVrfId]);

  useEffect(() => {
    setRouteTableStateByVrfId({});
  }, [routeTableContextKey]);

  const loadRouteTable = useCallback(
    async (vrf: L3DeviceVrfView) => {
      const action = vrf.routeTable;
      if (!action || !action.href) {
        return;
      }
      const requestKey = routeTableActionRequestKey(action);
      const current = routeTableStateByVrfId[vrf.id];
      if (current?.requestKey === requestKey && (current.status === 'loading' || current.status === 'ready')) {
        return;
      }
      setRouteTableStateByVrfId((prev) => ({
        ...prev,
        [vrf.id]: { requestKey, status: 'loading' }
      }));
      try {
        if (!requestTopologyAction) {
          throw new Error('Route table API is unavailable on this surface.');
        }
        if (!action.enabled) {
          throw new Error(action.reasonUnavailable || 'Route table action is unavailable.');
        }
        if (!isSafeRouteTableActionHref(action.href)) {
          throw new Error('Route table action URL is invalid.');
        }
        const response = await requestTopologyAction(action.href, { method: 'GET' });
        const payload = normalizeRouteTablePage(await response.json());
        setRouteTableStateByVrfId((prev) => ({
          ...prev,
          [vrf.id]: { requestKey, status: 'ready', data: payload }
        }));
      } catch (error) {
        setRouteTableStateByVrfId((prev) => ({
          ...prev,
          [vrf.id]: {
            requestKey,
            status: 'error',
            error: error instanceof Error ? error.message : 'Failed to load route table.'
          }
        }));
      }
    },
    [requestTopologyAction, routeTableStateByVrfId]
  );

  useEffect(() => {
    if (activePanel !== 'routeTable' || !activeVrf?.routeTable) {
      return;
    }
    const current = routeTableStateByVrfId[activeVrf.id];
    if (current?.requestKey === activeRouteTableRequestKey && (current.status === 'loading' || current.status === 'ready')) {
      return;
    }
    void loadRouteTable(activeVrf);
  }, [activePanel, activeRouteTableRequestKey, activeVrf, loadRouteTable, routeTableStateByVrfId]);

  return (
    <div className="topology-v2__device-node topology-v2__device-node--l3">
      <div className="topology-v2__device-label topology-v2__device-label--l3" title={label}>
        {label}
      </div>
      <div className="topology-v2__l3-vrf-list">
        {vrfs.map((vrf) => {
          const expanded = activeVrfId === vrf.id;
          return (
            <button
              key={vrf.id}
              type="button"
              className={[
                'topology-v2__l3-vrf-pill',
                expanded ? 'is-expanded' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              title={`${vrf.label} (${vrf.interfaces.length} L3 interfaces)`}
              onClick={(event) => {
                event.stopPropagation();
                setActiveVrfId((current) => (current === vrf.id ? null : vrf.id));
              }}
            >
              <span className="topology-v2__l3-vrf-pill-label">{vrf.label}</span>
              <span className="topology-v2__l3-vrf-pill-count">{vrf.interfaces.length}</span>
            </button>
          );
        })}
      </div>
      {activeVrf ? (
        <div
          className={[
            'topology-v2__l3-interface-panel',
            activePanel === 'routeTable' ? 'topology-v2__l3-interface-panel--route-table' : ''
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onWheelCapture={(event) => {
            event.stopPropagation();
          }}
        >
          <div className="topology-v2__l3-interface-panel-title">{activeVrf.label}</div>
          <div className="topology-v2__l3-panel-tabs">
            <button
              type="button"
              className={['topology-v2__l3-panel-tab', activePanel === 'interfaces' ? 'is-active' : ''].filter(Boolean).join(' ')}
              onClick={(event) => {
                event.stopPropagation();
                setActivePanel('interfaces');
              }}
            >
              Interfaces
            </button>
            {activeVrf.routeTable ? (
              <button
                type="button"
                disabled={!activeVrf.routeTable.enabled}
                title={activeVrf.routeTable.reasonUnavailable}
                className={['topology-v2__l3-panel-tab', activePanel === 'routeTable' ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={(event) => {
                  event.stopPropagation();
                  setActivePanel('routeTable');
                  void loadRouteTable(activeVrf);
                }}
              >
                {activeVrf.routeTable.label}
              </button>
            ) : null}
          </div>
          {activePanel === 'routeTable' && activeVrf.routeTable ? (
            <L3RouteTablePanel action={activeVrf.routeTable} state={activeRouteTableState} />
          ) : (
            <>
              <div className="topology-v2__l3-interface-list">
                {activeVrf.interfaces.length > 0 ? (
                  activeVrf.interfaces.map((intf) => (
                    <button
                      key={intf.id}
                      type="button"
                      className={[
                        'topology-v2__l3-interface-chip',
                        activeInterfaceId === intf.id ? 'is-selected' : ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={l3InterfaceChipStyle(intf.stateTone)}
                      onClick={(event) => {
                        event.stopPropagation();
                        setActiveInterfaceId((current) => (current === intf.id ? null : intf.id));
                      }}
                    >
                      {intf.label}
                    </button>
                  ))
                ) : (
                  <span className="topology-v2__l3-interface-empty">No L3 interfaces</span>
                )}
              </div>
              {activeInterface ? (
                <div className="topology-v2__l3-interface-detail">
                  <div className="topology-v2__l3-interface-detail-name">{activeInterface.label}</div>
                  <div className="topology-v2__l3-interface-detail-grid">
                    {topologyPropertyRows(activeInterface.properties).map((row) => (
                      <div
                        key={row.key}
                        className={`topology-v2__l3-interface-detail-row${row.group ? ' topology-v2__l3-interface-detail-row--group' : ''}`}
                        style={{ '--topology-property-depth': row.depth } as CSSProperties}
                      >
                        <span className="topology-v2__l3-interface-detail-key">{row.label}</span>
                        {!row.group ? <span className="topology-v2__l3-interface-detail-value topology-v2__topology-property-value">{row.value}</span> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

const L3SubnetNodeContent = ({ label }: { label: string }) => (
  <div className="topology-v2__subnet-node topology-v2__subnet-node--l3">
    <div className="topology-v2__subnet-label topology-v2__subnet-label--l3" title={label}>
      {label}
    </div>
  </div>
);

const overlayAccent = (ui: Record<string, unknown>): { stroke: string; fill: string; shadow: string } => {
  switch ((getString(ui.kind) ?? '').trim().toLowerCase()) {
    case 'path':
      return { stroke: '#0891b2', fill: 'rgba(207, 250, 254, 0.82)', shadow: 'rgba(8, 145, 178, 0.22)' };
    case 'focus':
      return { stroke: '#2563eb', fill: 'rgba(219, 234, 254, 0.78)', shadow: 'rgba(37, 99, 235, 0.22)' };
    case 'inspect':
      return { stroke: '#7c3aed', fill: 'rgba(237, 233, 254, 0.78)', shadow: 'rgba(124, 58, 237, 0.22)' };
    case 'hide':
      return { stroke: '#64748b', fill: 'rgba(241, 245, 249, 0.78)', shadow: 'rgba(100, 116, 139, 0.18)' };
    case 'highlight':
    default:
      return { stroke: '#f59e0b', fill: 'rgba(254, 243, 199, 0.82)', shadow: 'rgba(245, 158, 11, 0.24)' };
  }
};

const nodeOverlayStyle = (properties?: Record<string, unknown>): CSSProperties => {
  const ui = overlayUI(properties);
  if (!ui) {
    return {};
  }
  const style: CSSProperties = {};
  if (getBoolean(ui.dimmed)) {
    style.opacity = 0.28;
    style.filter = 'grayscale(0.45)';
  }
  if (getBoolean(ui.highlighted)) {
    const accent = overlayAccent(ui);
    style.opacity = 1;
    style.filter = 'none';
    style.border = `3px solid ${accent.stroke}`;
    style.background = accent.fill;
    style.boxShadow = `0 0 0 5px ${accent.shadow}`;
  }
  const colorOverlays = runtimeOverlaysWithEffect(properties, ['node_color']);
  if (colorOverlays.length > 0) {
    const accent = runtimeToneStyles[strongestRuntimeTone(colorOverlays)];
    style.background = accent.fill;
  }
  const ringOverlays = runtimeOverlaysWithEffect(properties, ['node_ring']);
  if (ringOverlays.length > 0) {
    const accent = runtimeToneStyles[strongestRuntimeTone(ringOverlays)];
    style.border = `3px solid ${accent.stroke}`;
    style.boxShadow = `0 0 0 5px ${accent.shadow}`;
  }
  return style;
};

const applyEdgeOverlayStyle = (style: Record<string, unknown>, edge: TopologyEdge): Record<string, unknown> => {
  const ui = overlayUI(edge.properties);
  if (!ui) {
    return style;
  }
  const next = { ...style };
  if (getBoolean(ui.dimmed)) {
    next.opacity = 0.22;
    next.stroke = '#cbd5e1';
  }
  if (getBoolean(ui.highlighted)) {
    const accent = overlayAccent(ui);
    next.opacity = 1;
    next.stroke = accent.stroke;
    next.strokeWidth = Math.max(typeof next.strokeWidth === 'number' ? next.strokeWidth : Number(next.strokeWidth) || 2, 4);
  }
  const colorOverlays = runtimeOverlaysWithEffect(edge.properties, ['edge_color']);
  if (colorOverlays.length > 0) {
    const accent = runtimeToneStyles[strongestRuntimeTone(colorOverlays)];
    next.opacity = 1;
    next.stroke = accent.stroke;
  }
  const widthOverlays = runtimeOverlaysWithEffect(edge.properties, ['edge_width']);
  if (widthOverlays.length > 0) {
    const numeric = widthOverlays.map((overlay) => getNumber(overlay.value)).find((value) => typeof value === 'number');
    next.strokeWidth = typeof numeric === 'number' ? Math.max(2, Math.min(8, numeric)) : 4;
  }
  return next;
};

const normalizeProviderEdgeBadgePorts = (value: unknown): ProviderEdgePortBadgeRecord[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const ports: ProviderEdgePortBadgeRecord[] = [];
  for (const item of value) {
    const record = asRecord(item);
    if (!record) {
      continue;
    }
    ports.push({
      interfaceName: getString(record.interfaceName) ?? getString(record.interface_name),
      classification: getString(record.classification)
    });
  }
  return ports;
};

export const topologyEdgeStrokeDasharrayForPattern = (pattern: unknown): string | null | undefined => {
  switch (normalizedString(pattern)) {
    case 'dashed':
      return '6 4';
    case 'dotted':
      return '2 6';
    case 'solid':
      return null;
    default:
      return undefined;
  }
};

export const applyTopologyEdgeStrokePattern = (
  edgeStyle: Record<string, unknown>,
  ui: Record<string, unknown> | undefined
): Record<string, unknown> => {
  const next = { ...edgeStyle };

  const applyPattern = (pattern: unknown) => {
    const dasharray = topologyEdgeStrokeDasharrayForPattern(pattern);
    if (dasharray === null) {
      delete next.strokeDasharray;
      return;
    }
    if (typeof dasharray === 'string' && dasharray.length > 0) {
      next.strokeDasharray = dasharray;
    }
  };

  applyPattern(ui?.strokePattern);

  const strokeDasharray = getString(ui?.strokeDasharray)?.trim();
  if (strokeDasharray) {
    next.strokeDasharray = strokeDasharray;
  }

  return next;
};

export const providerEdgeBadgeTone = (classification?: string): RuntimeTone => {
  const normalized = normalizedString(classification);
  if (normalized.startsWith('confirmed') || normalized.startsWith('probable')) {
    return 'success';
  }
  if (normalized.includes('possible')) {
    return 'warning';
  }
  if (normalized.includes('not_provider') || normalized.includes('insufficient')) {
    return 'normal';
  }
  return 'info';
};

const providerEdgeToneOrder: Record<RuntimeTone, number> = {
  critical: 4,
  success: 3,
  warning: 2,
  normal: 1,
  info: 0
};

export const strongestProviderEdgeBadgeTone = (classifications: Array<string | undefined>): RuntimeTone =>
  classifications.reduce<RuntimeTone>((best, classification) => {
    const tone = providerEdgeBadgeTone(classification);
    return providerEdgeToneOrder[tone] > providerEdgeToneOrder[best] ? tone : best;
  }, 'normal');

const formatProviderEdgeBadgeLabel = (classification?: string): string => {
  const normalized = (classification ?? '').trim();
  if (!normalized) {
    return 'Provider edge';
  }
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const providerEdgeBadges = (node: TopologyNode): RuntimeBadgeModel[] => {
  const properties = asRecord(node.properties);
  if (!properties) {
    return [];
  }
  const ports = normalizeProviderEdgeBadgePorts(properties.providerEdgePorts);
  if (ports.length > 0) {
    const summary = asRecord(properties.providerEdgeSummary);
    const count = getNumber(summary?.count) ?? ports.length;
    const strongest = strongestProviderEdgeBadgeTone(ports.map((port) => port.classification));
    return [
      {
        text: `${count} provider edge ${count === 1 ? 'port' : 'ports'}`,
        tone: strongest
      }
    ];
  }
  const port = asRecord(properties.providerEdgePort);
  if (!port) {
    return [];
  }
  const classification = getString(port.classification);
  const interfaceName = getString(port.interfaceName) ?? getString(port.interface_name);
  return [
    {
      text: interfaceName ? `${interfaceName}: ${formatProviderEdgeBadgeLabel(classification)}` : formatProviderEdgeBadgeLabel(classification),
      tone: providerEdgeBadgeTone(classification)
    }
  ];
};

const dedupeBadges = (badges: RuntimeBadgeModel[]): RuntimeBadgeModel[] => {
  const seen = new Set<string>();
  return badges.filter((badge) => {
    const key = `${badge.text}|${badge.tone}|${badge.deliveryMode ?? ''}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const pickEdgeLinkStateStroke = (
  edge: TopologyEdge,
  ui: Record<string, unknown>,
  nodeById: Map<string, TopologyNode>
): string | undefined => {
  const linkState = asRecord(ui.linkState);
  if (!linkState) {
    return undefined;
  }
  const source = getString(linkState.source);
  if (source !== 'interface') {
    return undefined;
  }
  const labelKey = getString(linkState.label) ?? 'state.oper';
  const matchValue = getString(linkState.value) ?? 'down';
  const color = getString(linkState.color) ?? '#ef4444';
  const anyEndpoint = linkState.anyEndpoint !== false;

  const connectionsRaw = asRecord(edge.properties ?? {})?.connections;
  if (!Array.isArray(connectionsRaw) || connectionsRaw.length === 0) {
    const fromNode = nodeById.get(edge.fromId);
    const toNode = nodeById.get(edge.toId);
    if (!fromNode && !toNode) {
      return undefined;
    }
    const fromState = getTopologyPropertyString(fromNode?.properties, labelKey) ?? fromNode?.labels?.[labelKey];
    const toState = getTopologyPropertyString(toNode?.properties, labelKey) ?? toNode?.labels?.[labelKey];
    const match =
      anyEndpoint ? fromState === matchValue || toState === matchValue : fromState === matchValue && toState === matchValue;
    return match ? color : undefined;
  }

  let anyMatch = false;
  let sawEndpoint = false;
  for (const conn of connectionsRaw) {
    const connRec = asRecord(conn) ?? {};
    const fromId = getString(connRec.fromInterfaceId);
    const toId = getString(connRec.toInterfaceId);
    const fromNode = fromId ? nodeById.get(fromId) : undefined;
    const toNode = toId ? nodeById.get(toId) : undefined;
    const fromState = getTopologyPropertyString(fromNode?.properties, labelKey) ?? fromNode?.labels?.[labelKey];
    const toState = getTopologyPropertyString(toNode?.properties, labelKey) ?? toNode?.labels?.[labelKey];
    if (fromNode || toNode) {
      sawEndpoint = true;
    }
    if (anyEndpoint) {
      if (fromState === matchValue || toState === matchValue) {
        anyMatch = true;
        break;
      }
    } else if (fromState === matchValue && toState === matchValue) {
      anyMatch = true;
      break;
    }
  }

  if (!sawEndpoint || !anyMatch) {
    return undefined;
  }
  return color;
};

const DEVICE_NODE_WIDTH = 220;
const DEVICE_NODE_HEIGHT_COMPACT = 120;
const DEVICE_NODE_HEIGHT_EXPANDED = 160;

const PORT_DOT_SIZE = 10;
const PORT_PITCH = 14;
const PORT_SIDE_PADDING = 10;
const DEVICE_LABEL_BAND_HEIGHT = 26;
const LAG_BUNDLE_CUSHION = 28;
const PORT_HIT_TARGET_SIZE = 22;
const LAG_HIT_TARGET_SIZE = 34;

const elk = new ELK();

type DiagramNodeData = EdgeChipNodeData;

export interface TopologyCanvasProps {
  topology: TopologyGraph | null;
  graphKey: string;
  pickNodeUI?: (node: TopologyNode) => Record<string, unknown>;
  pickEdgeUI?: (edge: TopologyEdge) => Record<string, unknown>;
  overlayKeyByNodeId?: Record<string, string | undefined>;
  overlayKeyByEdgeId?: Record<string, string | undefined>;
  selectedNodeIds?: Set<string>;
  selectedEdgeIds?: Set<string>;
  onNodeSelect?: (event: React.MouseEvent, nodeId: string) => void;
  onEdgeSelect?: (event: React.MouseEvent, edgeId: string) => void;
  onNodeDoubleClick?: (event: React.MouseEvent, nodeId: string) => void;
  onEdgeDoubleClick?: (event: React.MouseEvent, edgeId: string) => void;
  onPaneClick?: (event: React.MouseEvent) => void;
  showControls?: boolean;
  requestTopologyAction?: (href: string, init?: RequestInit) => Promise<Response>;
}

type PortAnchor = { x: number; y: number; side: PortSide; text: string; portId: string; slot?: number };

type PortLinkEdgeData = {
  sourcePort?: PortAnchor;
  targetPort?: PortAnchor;
  label?: string;
  style?: Record<string, unknown>;
  overlayKey?: string;
};

type PortDecorEdgeData = {
  ports: PortAnchor[];
  kindByPortId: Record<string, string | undefined>;
  uiByPortId: Record<string, Record<string, unknown> | undefined>;
  hidePortIds?: Record<string, true>;
  overlayKey?: string;
};

type LagBundleEdgeData = {
  lag: PortAnchor;
  members: PortAnchor[];
  style?: Record<string, unknown>;
  overlayKey?: string;
};

type ParallelEdgeData = {
  label?: string;
  style?: Record<string, unknown>;
  offsetIndex: number;
  offsetTotal: number;
  overlayKey?: string;
};

const PARALLEL_EDGE_GAP = 30;

const ParallelEdge = ({ id, sourceX, sourceY, targetX, targetY, markerEnd, data }: EdgeProps<ParallelEdgeData>) => {
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const length = Math.max(1, Math.hypot(dx, dy));
  const normalX = -dy / length;
  const normalY = dx / length;
  const total = Math.max(1, data?.offsetTotal ?? 1);
  const index = data?.offsetIndex ?? 0;
  const offset = total > 1 ? (index - (total - 1) / 2) * PARALLEL_EDGE_GAP : 0;
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;
  const controlX = midX + normalX * offset;
  const controlY = midY + normalY * offset;
  const path = `M ${sourceX},${sourceY} Q ${controlX},${controlY} ${targetX},${targetY}`;
  const labelX = controlX;
  const labelY = controlY;
  const style = (data?.style ?? {}) as CSSProperties;

  return (
    <>
      <BaseEdge id={id} path={path} style={style} markerEnd={markerEnd} />
      {data?.label ? (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 11,
              fontWeight: 700,
              color: '#312e81',
              background: 'rgba(238, 242, 255, 0.95)',
              padding: '2px 4px',
              borderRadius: 4,
              pointerEvents: 'none'
            }}
          >
            {data.label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
};

const PortLinkEdge = ({ id, source, target, data }: EdgeProps<PortLinkEdgeData>) => {
  const sourceNode = useStore((s: any) => s.nodeInternals.get(source));
  const targetNode = useStore((s: any) => s.nodeInternals.get(target));

  if (!sourceNode || !targetNode || !data) {
    return null;
  }

  const sourceX =
    sourceNode.positionAbsolute.x +
    (data.sourcePort ? data.sourcePort.x + PORT_DOT_SIZE / 2 : (Number(sourceNode.width) || DEVICE_NODE_WIDTH) / 2);
  const sourceY =
    sourceNode.positionAbsolute.y +
    (data.sourcePort ? data.sourcePort.y + PORT_DOT_SIZE / 2 : (Number(sourceNode.height) || DEVICE_NODE_HEIGHT_COMPACT) / 2);
  const targetX =
    targetNode.positionAbsolute.x +
    (data.targetPort ? data.targetPort.x + PORT_DOT_SIZE / 2 : (Number(targetNode.width) || DEVICE_NODE_WIDTH) / 2);
  const targetY =
    targetNode.positionAbsolute.y +
    (data.targetPort ? data.targetPort.y + PORT_DOT_SIZE / 2 : (Number(targetNode.height) || DEVICE_NODE_HEIGHT_COMPACT) / 2);

  const [path] = getStraightPath({ sourceX, sourceY, targetX, targetY });

  const stroke = typeof (data.style as any)?.stroke === 'string' ? String((data.style as any).stroke) : '#6366f1';
  const strokeWidth =
    typeof (data.style as any)?.strokeWidth === 'number' ? Number((data.style as any).strokeWidth) : 2;
  const strokeDasharray =
    typeof (data.style as any)?.strokeDasharray === 'string' ? String((data.style as any).strokeDasharray) : undefined;
  const opacity = typeof (data.style as any)?.opacity === 'number' ? Number((data.style as any).opacity) : undefined;

  return (
    <>
      <BaseEdge id={id} path={path} style={{ stroke, strokeWidth, strokeDasharray, opacity }} />
      {data.label ? (
        <EdgeLabelRenderer>
          <div
            className="topology-v2__runtime-edge-label nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${(sourceX + targetX) / 2}px, ${(sourceY + targetY) / 2}px)`,
              pointerEvents: 'none'
            }}
          >
            {data.label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
};

const PortDecorEdge = ({ source, data }: EdgeProps<PortDecorEdgeData>) => {
  const node = useStore((s: any) => s.nodeInternals.get(source));
  if (!node || !data) {
    return null;
  }

  const shouldHide = (portId: string) => Boolean(data.hidePortIds?.[portId]);

  const labelOffset = 14;
  const labelPos = (side: PortSide, slot = 0, configuredStagger?: unknown) => {
    const staggerStep =
      typeof configuredStagger === 'number'
        ? configuredStagger
        : typeof configuredStagger === 'string'
          ? Number(configuredStagger)
          : 10;
    const stagger = slot % 2 === 0 ? 0 : staggerStep;
    switch (side) {
      case 'left':
        return { transform: 'translate(-100%, -50%)', left: -(labelOffset + stagger), top: 0 };
      case 'right':
        return { transform: 'translate(0%, -50%)', left: labelOffset + stagger, top: 0 };
      case 'top':
        return { transform: 'translate(-50%, -100%)', left: 0, top: -(labelOffset + stagger) };
      case 'bottom':
        return { transform: 'translate(-50%, 0%)', left: 0, top: labelOffset + stagger };
    }
  };

  return (
    <EdgeLabelRenderer>
      {(data.ports ?? [])
        .filter((port) => !shouldHide(port.portId))
        .map((port) => {
          const kind = data.kindByPortId?.[port.portId];
          const portUI = data.uiByPortId?.[port.portId];
          const x = node.positionAbsolute.x + port.x + PORT_DOT_SIZE / 2;
          const y = node.positionAbsolute.y + port.y + PORT_DOT_SIZE / 2;
          const isLag = kind === 'lag';
          const portStyle = asRecord(portUI?.portStyle);
          const style = asRecord(portUI?.style);
          const showLabel = portUI?.showPortLabel !== false && portUI?.portLabel !== false && portUI?.showLabel !== false;
          const portColor =
            getString(portStyle?.color) ??
            getString(portUI?.portColor) ??
            getString(portUI?.color) ??
            getString(style?.color) ??
            getString(style?.stroke) ??
            '#4f46e5';
          return (
            <div
              key={port.portId}
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
                pointerEvents: 'none'
              }}
            >
              <div
                style={{
                  width: PORT_DOT_SIZE,
                  height: PORT_DOT_SIZE,
                  borderRadius: 999,
                  background: isLag ? 'transparent' : portColor,
                  border: isLag ? `2px solid ${portColor}` : '1px solid rgba(255,255,255,0.95)',
                  boxShadow: '0 2px 6px rgba(15,23,42,0.18)'
                }}
              />
              {showLabel && (
                <div
                  className="topology-v2__port-label"
                  style={{ position: 'absolute', ...labelPos(port.side, port.slot ?? 0, portUI?.labelStagger) }}
                >
                  {port.text}
                </div>
              )}
            </div>
          );
        })}
    </EdgeLabelRenderer>
  );
};

const LagBundleEdge = ({ source, data }: EdgeProps<LagBundleEdgeData>) => {
  const node = useStore((s: any) => s.nodeInternals.get(source));
  if (!node || !data) {
    return null;
  }

  const stroke = typeof (data.style as any)?.stroke === 'string' ? String((data.style as any).stroke) : '#6366f1';
  const strokeWidth =
    typeof (data.style as any)?.strokeWidth === 'number' ? Number((data.style as any).strokeWidth) : 2;
  const strokeDasharray =
    typeof (data.style as any)?.strokeDasharray === 'string' ? String((data.style as any).strokeDasharray) : undefined;
  const opacity = typeof (data.style as any)?.opacity === 'number' ? Number((data.style as any).opacity) : undefined;

  const ringR = 16;
  const lagX = node.positionAbsolute.x + data.lag.x + PORT_DOT_SIZE / 2;
  const lagY = node.positionAbsolute.y + data.lag.y + PORT_DOT_SIZE / 2;

  const memberPaths = (data.members ?? []).map((m) => {
    const mx = node.positionAbsolute.x + m.x + PORT_DOT_SIZE / 2;
    const my = node.positionAbsolute.y + m.y + PORT_DOT_SIZE / 2;
    const dx = mx - lagX;
    const dy = my - lagY;
    const dist = Math.max(1, Math.hypot(dx, dy));
    const ex = lagX + (dx / dist) * ringR;
    const ey = lagY + (dy / dist) * ringR;
    const cx = (mx + ex) / 2;
    const cy = (my + ey) / 2;
    const nx = -dy / dist;
    const ny = dx / dist;
    const bend = 18;
    const qx = cx + nx * bend;
    const qy = cy + ny * bend;
    return `M ${mx} ${my} Q ${qx} ${qy} ${ex} ${ey}`;
  });

  return (
    <>
      {memberPaths.map((d, idx) => (
        <BaseEdge
          key={idx}
          id={`${source}-lagbundle-${idx}`}
          path={d}
          style={{ stroke, strokeWidth, strokeDasharray, opacity }}
        />
      ))}
      <circle cx={lagX} cy={lagY} r={ringR} fill="transparent" stroke={stroke} strokeWidth={2} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${lagX}px, ${lagY}px)`,
            pointerEvents: 'none'
          }}
        >
          <div
            className="topology-v2__port-label"
            style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -50%)' }}
          >
            {data.lag.text}
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

const noopNodeHandler = () => undefined;
const noopEdgeHandler = () => undefined;
const noopPaneHandler = () => undefined;
const emptyNodeUI = () => ({});
const emptyEdgeUI = () => ({});
const emptyOverlayKeys: Record<string, string | undefined> = Object.freeze({});
const emptySelectedIds = new Set<string>();

type SelectableTopologyElement = { id: string; selected?: boolean };

/** Applies selection to React Flow elements without rebuilding their layout. */
export const applyTopologySelection = <T extends SelectableTopologyElement>(
  elements: T[],
  selectedIds: Set<string>
): T[] => {
  if (selectedIds.size === 0) {
    return elements;
  }
  return elements.map((element) => ({ ...element, selected: selectedIds.has(element.id) }));
};

/** Returns whether an asynchronous layout is still allowed to commit its nodes. */
export const topologyLayoutBuildIsCurrent = (buildRevision: number, currentRevision: number): boolean =>
  buildRevision === currentRevision;

const FitViewWhenReady = ({
  graphKey,
  nodeCount,
  containerRef
}: {
  graphKey: string;
  nodeCount: number;
  containerRef: RefObject<HTMLDivElement>;
}) => {
  const reactFlow = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const frameRef = useRef<number | null>(null);

  const fit = useCallback(() => {
    if (typeof window === 'undefined' || nodeCount === 0 || !nodesInitialized) {
      return;
    }
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      reactFlow.fitView({ padding: 0.18, duration: 0 });
    });
  }, [nodeCount, nodesInitialized, reactFlow]);

  useEffect(() => {
    fit();
  }, [fit, graphKey]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => {
      fit();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [containerRef, fit]);

  useEffect(() => {
    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, []);

  return null;
};

export const TopologyCanvas = ({
  topology,
  graphKey,
  pickNodeUI = emptyNodeUI,
  pickEdgeUI = emptyEdgeUI,
  overlayKeyByNodeId = emptyOverlayKeys,
  overlayKeyByEdgeId = emptyOverlayKeys,
  selectedNodeIds = emptySelectedIds,
  selectedEdgeIds = emptySelectedIds,
  onNodeSelect = noopNodeHandler,
  onEdgeSelect = noopEdgeHandler,
  onNodeDoubleClick = noopNodeHandler,
  onEdgeDoubleClick = noopEdgeHandler,
  onPaneClick = noopPaneHandler,
  showControls = true,
  requestTopologyAction
}: TopologyCanvasProps) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<DiagramNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const lastLayoutKeyRef = useRef<string | null>(null);
  const layoutRevisionRef = useRef(0);
  const devicePositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const handledPointerRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  type HoverTarget = { kind: 'node' | 'edge'; id: string };
  const [hoverTarget, setHoverTarget] = useState<HoverTarget | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);

  const topologyNodeById = useMemo(() => {
    const map = new Map<string, TopologyNode>();
    (topology?.nodes ?? []).forEach((n) => map.set(n.id, n));
    return map;
  }, [topology?.nodes]);

  const topologyEdgeById = useMemo(() => {
    const map = new Map<string, TopologyEdge>();
    (topology?.edges ?? []).forEach((e) => map.set(e.id, e));
    return map;
  }, [topology?.edges]);

  const updateHoverPos = useCallback((event: React.MouseEvent) => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const rect = container.getBoundingClientRect();
    const x = event.clientX - rect.left + 12;
    const y = event.clientY - rect.top + 12;
    setHoverPos({ x, y });
  }, []);

  const suppressNodeHoverTooltip = useCallback((target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }
    return Boolean(
      target.closest(
        '.topology-v2__l3-vrf-list, .topology-v2__l3-vrf-pill, .topology-v2__l3-interface-panel, .topology-v2__l3-interface-chip, .topology-v2__l3-interface-detail, .topology-v2__l3-panel-tab, .topology-v2__l3-route-table-wrap, .topology-v2__l3-route-table'
      )
    );
  }, []);

  const hoverContent = useMemo(() => {
    if (!hoverTarget) {
      return null;
    }

    const buildRows = (properties: Record<string, unknown> | undefined) => {
      const runtimeRows = runtimeOverlaysWithEffect(properties, ['hover_rows'])
        .map((overlay) => ({ key: `runtime:${overlay.id}`, label: overlay.label, value: formatRuntimeValue(overlay) }))
        .filter((row) => row.label.trim().length > 0 && row.value.trim().length > 0);
      return topologyHoverPropertyRows(properties, runtimeRows);
    };

    if (hoverTarget.kind === 'node') {
      const node = topologyNodeById.get(hoverTarget.id);
      if (!node) {
        return null;
      }
      const { rows, truncated } = buildRows(node.properties);
      return {
        title: topologyPresentationString(node.properties, 'hoverTitle', node.displayName ?? node.id),
        rows,
        truncated
      };
    }

    const edge = topologyEdgeById.get(hoverTarget.id);
    if (!edge) {
      return null;
    }
    const { rows, truncated } = buildRows(edge.properties);
    const relation = edge.labels?.relation ? ` (${edge.labels.relation})` : '';
    return {
      title: topologyPresentationString(edge.properties, 'hoverTitle', `${edge.id}${relation}`),
      rows,
      truncated
    };
  }, [hoverTarget, topologyEdgeById, topologyNodeById]);

  const edgeTypes = useMemo(
    () => ({ portlink: PortLinkEdge, portdecor: PortDecorEdge, lagbundle: LagBundleEdge, parallel: ParallelEdge }),
    []
  );

  const rfNodes = useMemo(() => {
    return applyTopologySelection(nodes, selectedNodeIds);
  }, [nodes, selectedNodeIds]);

  const rfEdges = useMemo(() => {
    return applyTopologySelection(edges, selectedEdgeIds);
  }, [edges, selectedEdgeIds]);

  useEffect(() => {
    const next: Record<string, { x: number; y: number }> = {};
    nodes.forEach((n) => {
      if (n.parentNode) {
        return;
      }
      next[n.id] = { x: n.position.x, y: n.position.y };
    });
    devicePositionsRef.current = next;
  }, [nodes]);

  // Selection is applied to rfNodes/rfEdges above, so it must not restart ELK.
  useEffect(() => {
    if (!topology) {
      setNodes([]);
      setEdges([]);
      return;
    }

    // Layout is asynchronous. Chat/session updates can replace the graph while
    // ELK is still working; an older layout must not overwrite the newer graph.
    let cancelled = false;
    const buildRevision = layoutRevisionRef.current + 1;
    layoutRevisionRef.current = buildRevision;

    const topologyGraphId = typeof topology.graphId === 'string' && topology.graphId.trim().length > 0 ? topology.graphId.trim() : 'all';
    const layoutScopeKey = `${graphKey}:${topologyGraphId}`;
    const visibleTopologyNodes = topology.nodes ?? [];
    const visibleTopologyEdges = topology.edges ?? [];
    const nodeById = new Map<string, TopologyNode>();
    visibleTopologyNodes.forEach((n) => nodeById.set(n.id, n));

    const embedParents = new Map<string, string>();
    const nonEmbedEdges: TopologyEdge[] = [];
    visibleTopologyEdges.forEach((edge) => {
      const ui = pickEdgeUI(edge);
      const style = typeof ui.style === 'string' ? ui.style : 'link';
      if (style === 'embed' && edge.directed) {
        embedParents.set(edge.toId, edge.fromId);
        return;
      }
      nonEmbedEdges.push(edge);
    });

    const childrenByParent = new Map<string, string[]>();
    embedParents.forEach((parentId, childId) => {
      const list = childrenByParent.get(parentId) ?? [];
      list.push(childId);
      childrenByParent.set(parentId, list);
    });

    const nodeUIById = new Map<string, Record<string, unknown>>();
    visibleTopologyNodes.forEach((n) => {
      nodeUIById.set(n.id, { ...pickNodeUI(n), ...(topologyPresentationProps(n.properties) ?? {}) });
    });
    const topLevelNodeIds = visibleTopologyNodes.map((n) => n.id).filter((id) => !embedParents.has(id));
    const l3MembershipGraph = isL3MembershipGraph(topology);
    const l3VrfsByDeviceId = new Map<string, L3DeviceVrfView[]>();
    if (l3MembershipGraph) {
      topLevelNodeIds.forEach((id) => {
        const deviceVrfIds = (childrenByParent.get(id) ?? []).filter((childId) => isL3VrfNode(nodeById.get(childId)));
        const vrfs = deviceVrfIds
          .map((vrfId): L3DeviceVrfView | null => {
            const vrfNode = nodeById.get(vrfId);
            if (!vrfNode) {
              return null;
            }
            const interfaces = (childrenByParent.get(vrfId) ?? [])
              .map((intfId) => nodeById.get(intfId))
              .filter((intf): intf is TopologyNode => intf !== undefined && isL3InterfaceNode(intf))
              .map((intf) => {
                return {
                  id: intf.id,
                  label: topologyPresentationString(intf.properties, 'title', intf.displayName ?? intf.id),
                  adminState: normalizeInterfaceStateLabel(
                    getTopologyPropertyString(intf.properties, 'state.admin') ?? intf.labels?.['state.admin']
                  ),
                  operState: normalizeInterfaceStateLabel(
                    getTopologyPropertyString(intf.properties, 'state.oper') ?? intf.labels?.['state.oper']
                  ),
                  stateTone: l3InterfaceStateTone(
                    getTopologyPropertyString(intf.properties, 'state.admin') ?? intf.labels?.['state.admin'],
                    getTopologyPropertyString(intf.properties, 'state.oper') ?? intf.labels?.['state.oper']
                  ),
                  properties: intf.properties ?? {}
                };
              })
              .sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true, sensitivity: 'base' }));
            return {
              id: vrfId,
              label: topologyPresentationString(vrfNode.properties, 'title', vrfNode.displayName ?? vrfId),
              interfaces,
              routeTable: routeTableActionFromProperties(vrfNode.properties)
            };
          })
          .filter((vrf): vrf is L3DeviceVrfView => vrf !== null)
          .sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true, sensitivity: 'base' }));
        l3VrfsByDeviceId.set(id, vrfs);
      });
    }

    const isEdgeChipNode = (id: string): boolean => isEdgeChipRender(nodeUIById.get(id));
    const topAncestorFor = (id: string): string => topAncestorByNodeId(id, embedParents);
    const visualPortParentId = (id: string): string | undefined => {
      const parentId = embedParents.get(id);
      if (!parentId) {
        return undefined;
      }
      if (l3MembershipGraph && isL3InterfaceNode(nodeById.get(id))) {
        return undefined;
      }
      return parentId;
    };

    const deviceDimensions = new Map<string, { width: number; height: number }>();
    topLevelNodeIds.forEach((id) => {
      const node = nodeById.get(id);
      if (l3MembershipGraph && isL3SubnetNode(node)) {
        deviceDimensions.set(id, { width: L3_SUBNET_NODE_SIZE, height: L3_SUBNET_NODE_SIZE });
        return;
      }
      const uiProps = topologyPresentationProps(node?.properties);
      const embedCounts = asRecord(uiProps?.embedCounts);
      const chipCount = Object.values(embedCounts ?? {}).filter(
        (value) => typeof value === 'number' && value > 0
      ).length;
      const chipRows = chipCount > 0 ? Math.ceil(chipCount / 3) : 0;
      const chipHeight = chipRows > 0 ? chipRows * 22 + 16 : 0;
      const runtimeBadgeCount = runtimeOverlaysWithEffect(node?.properties, ['node_badge']).length;
      const badgeCount = (Array.isArray(uiProps?.badges) ? uiProps.badges.length : 0) + runtimeBadgeCount;
      const renderedBadgeCount = badgeCount <= 2 ? badgeCount : 3;
      const badgeRows = renderedBadgeCount;
      const presentationHeight = (getString(uiProps?.subtitle) || getString(uiProps?.nodeSubtitle) ? 18 : 0) + badgeRows * 28;
      const portCount = (childrenByParent.get(id) ?? []).filter((childId) => !isEdgeChipNode(childId)).length;
      const l3Vrfs = l3VrfsByDeviceId.get(id) ?? [];
      const height = l3MembershipGraph && l3Vrfs.length > 0
        ? l3DeviceHeight(l3Vrfs.length)
        : portCount > 0
          ? Math.max(
              DEVICE_NODE_HEIGHT_EXPANDED,
              DEVICE_LABEL_BAND_HEIGHT + PORT_SIDE_PADDING * 2 + Math.ceil(portCount / 2) * PORT_PITCH
            )
          : DEVICE_NODE_HEIGHT_COMPACT + chipHeight + presentationHeight;
      deviceDimensions.set(id, { width: DEVICE_NODE_WIDTH, height });
    });

    const layoutEdgePairs = new Map<string, { source: string; target: string }>();
    nonEmbedEdges.forEach((edge) => {
      const source = topAncestorFor(edge.fromId);
      const target = topAncestorFor(edge.toId);
      if (!source || !target || source === target) {
        return;
      }
      const key = source < target ? `${source}--${target}` : `${target}--${source}`;
      if (!layoutEdgePairs.has(key)) {
        layoutEdgePairs.set(key, { source, target });
      }
    });

    const build = async () => {
      const topPositions = new Map<string, { x: number; y: number }>();
      const reusePositions = lastLayoutKeyRef.current === layoutScopeKey;
      if (reusePositions) {
        topLevelNodeIds.forEach((id) => {
          const pos = devicePositionsRef.current[id];
          if (pos) {
            topPositions.set(id, pos);
          }
        });
      }

      if (topPositions.size === 0) {
        const elkGraph = {
          id: 'root',
          layoutOptions: {
            'elk.algorithm': 'layered',
            'elk.direction': 'DOWN',
            'elk.layered.spacing.nodeNodeBetweenLayers': '80',
            'elk.spacing.nodeNode': '90',
            'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
            'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP'
          },
          children: topLevelNodeIds.map((id) => {
            const dims = deviceDimensions.get(id) ?? { width: DEVICE_NODE_WIDTH, height: DEVICE_NODE_HEIGHT_COMPACT };
            return { id, width: dims.width, height: dims.height };
          }),
          edges: Array.from(layoutEdgePairs.values()).map((pair, idx) => ({
            id: `e-${idx}`,
            sources: [pair.source],
            targets: [pair.target]
          }))
        };

        try {
          const layout = await elk.layout(elkGraph as any);
          if (cancelled || !topologyLayoutBuildIsCurrent(buildRevision, layoutRevisionRef.current)) {
            return;
          }
          (layout.children ?? []).forEach((child: any) => {
            topPositions.set(String(child.id), { x: Number(child.x ?? 0), y: Number(child.y ?? 0) });
          });
        } catch {
          // Fall back to a simple deterministic grid so a resolved graph never renders as an empty canvas.
          topLevelNodeIds.forEach((id, index) => {
            const column = index % 3;
            const row = Math.floor(index / 3);
            topPositions.set(id, {
              x: column * (DEVICE_NODE_WIDTH + 80),
              y: row * (DEVICE_NODE_HEIGHT_COMPACT + 80)
            });
          });
        }
      }

      topLevelNodeIds.forEach((id, index) => {
        if (topPositions.has(id)) {
          return;
        }
        const column = index % 3;
        const row = Math.floor(index / 3);
        topPositions.set(id, {
          x: column * (DEVICE_NODE_WIDTH + 80),
          y: row * (DEVICE_NODE_HEIGHT_COMPACT + 80)
        });
      });

      const nextNodes: Node<DiagramNodeData>[] = [];
      visibleTopologyNodes.forEach((n) => {
        const ui = pickNodeUI(n);
        const uiProps = topologyPresentationProps(n.properties);
        const text = topologyPresentationString(n.properties, 'title', n.displayName ?? n.id);
        const policySummary = inferTopologyPolicyNodeSummary(n.labels, n.properties);
        const subtitle = getString(uiProps?.subtitle) ?? getString(uiProps?.nodeSubtitle) ?? policySummary.subtitle;
        const presentationBadges = Array.isArray(uiProps?.badges)
          ? uiProps.badges
              .map(
                (badge): RuntimeBadgeModel => ({
                  text: formatTopologyPresentationValue(badge),
                  tone: 'normal',
                  deliveryMode: undefined
                })
              )
              .filter((badge) => badge.text.trim().length > 0)
          : [];
        const inferredBadges = policySummary.badges.map(
          (badge): RuntimeBadgeModel => ({
            text: badge,
            tone: 'normal',
            deliveryMode: undefined
          })
        );
        const runtimeBadges = runtimeOverlaysWithEffect(n.properties, ['node_badge'])
          .map(runtimeBadgeModel)
          .sort((left, right) => {
            const byDelivery = runtimeDeliveryRank(left.deliveryMode) - runtimeDeliveryRank(right.deliveryMode);
            return byDelivery !== 0 ? byDelivery : left.text.localeCompare(right.text);
          });
        const allBadges = dedupeBadges([...providerEdgeBadges(n), ...runtimeBadges, ...presentationBadges, ...inferredBadges]);
        const badges =
          allBadges.length <= 2
            ? allBadges
            : [
                ...allBadges.slice(0, 2),
                {
                  text: `+${allBadges.length - 2} more`,
                  tone: 'normal' as RuntimeTone,
                  deliveryMode: undefined as RuntimeOverlay['deliveryMode']
                }
              ];
        const embedCounts = asRecord(uiProps?.embedCounts);
        const embedChips = Object.entries(embedCounts ?? {})
          .map(([label, raw]) => ({ label, count: typeof raw === 'number' ? raw : Number(raw) }))
          .filter((item) => Number.isFinite(item.count) && item.count > 0)
          .sort((left, right) => left.label.localeCompare(right.label));
        const parentId = embedParents.get(n.id);
        if (parentId) {
          return;
        }
        const dims = deviceDimensions.get(n.id) ?? { width: DEVICE_NODE_WIDTH, height: DEVICE_NODE_HEIGHT_COMPACT };
        const pos = topPositions.get(n.id) ?? { x: 0, y: 0 };
        const l3Vrfs = l3VrfsByDeviceId.get(n.id) ?? [];
        const l3DeviceNode = l3MembershipGraph && l3Vrfs.length > 0;
        const l3SubnetNode = l3MembershipGraph && isL3SubnetNode(n);
        nextNodes.push({
          id: n.id,
          position: pos,
          draggable: true,
          selectable: true,
          data: {
            text,
            label: l3DeviceNode ? (
              <L3DeviceNodeContent
                label={text}
                vrfs={l3Vrfs}
                routeTableContextKey={graphKey}
                requestTopologyAction={requestTopologyAction}
              />
            ) : l3SubnetNode ? (
              <L3SubnetNodeContent label={text} />
            ) : (
              <div className="topology-v2__device-node">
                <div className="topology-v2__device-label">{text}</div>
                {subtitle && (
                  <div className="topology-v2__device-subtitle">{subtitle}</div>
                )}
                {badges.length > 0 && (
                  <div className="topology-v2__device-badge-list">
                    {badges.map((badge, index) => (
                      <span
                        key={`${n.id}:badge:${index}:${badge.text}`}
                        className={`topology-v2__runtime-badge topology-v2__runtime-badge--${badge.tone}`}
                        title={badge.text}
                      >
                        <span className="topology-v2__runtime-badge-text">
                          {badge.label && badge.value ? (
                            <>
                              <span className="topology-v2__runtime-badge-label">{badge.label}</span>
                              <span className="topology-v2__runtime-badge-separator">:</span>
                              <span className="topology-v2__runtime-badge-value">{badge.value}</span>
                            </>
                          ) : (
                            <span className="topology-v2__runtime-badge-label">{badge.text}</span>
                          )}
                        </span>
                        {badge.deliveryMode ? (
                          <span
                            className={`topology-v2__runtime-badge-delivery topology-v2__runtime-badge-delivery--${badge.deliveryMode}`}
                            title={badge.deliveryMode === 'live' ? 'Live annotation' : 'Fixed annotation'}
                          >
                            {runtimeDeliveryIcon(badge.deliveryMode)}
                            {badge.deliveryMode === 'live' ? 'Live' : 'Fixed'}
                          </span>
                        ) : null}
                      </span>
                    ))}
                  </div>
                )}
                {embedChips.length > 0 && (
                  <div className="topology-v2__device-chip-list">
                    {embedChips.map((chip) => (
                      <span
                        key={`${n.id}:${chip.label}`}
                        title={`${chip.label}: ${chip.count}`}
                        className="topology-v2__device-chip"
                      >
                        {chip.label} {chip.count}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ),
            ui,
            overlayKey: overlayKeyByNodeId[n.id]
          },
          className: l3DeviceNode
            ? 'topology-v2__node--device topology-v2__node--device-l3'
            : l3SubnetNode
              ? 'topology-v2__node--subnet-l3'
              : 'topology-v2__node--device',
          style: {
            width: dims.width,
            height: dims.height,
            borderRadius: l3SubnetNode ? '999px' : 12,
            border: l3SubnetNode ? '2px solid rgba(37, 99, 235, 0.42)' : '2px solid #4c1d95',
            background: l3SubnetNode
              ? 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.98), rgba(219, 234, 254, 0.96) 55%, rgba(191, 219, 254, 0.92))'
              : 'rgba(238, 242, 255, 0.28)',
            overflow: 'visible',
            ...nodeOverlayStyle(n.properties)
          }
        });
      });

      const edgesByNode = new Map<string, TopologyEdge[]>();
      nonEmbedEdges.forEach((edge) => {
        const a = edgesByNode.get(edge.fromId) ?? [];
        a.push(edge);
        edgesByNode.set(edge.fromId, a);
        const b = edgesByNode.get(edge.toId) ?? [];
        b.push(edge);
        edgesByNode.set(edge.toId, b);
      });

      const topNodeBoxes = new Map<string, DeviceBox>();
      topLevelNodeIds.forEach((id) => {
        const dims = deviceDimensions.get(id) ?? { width: DEVICE_NODE_WIDTH, height: DEVICE_NODE_HEIGHT_COMPACT };
        const pos = topPositions.get(id) ?? { x: 0, y: 0 };
        topNodeBoxes.set(id, {
          x: pos.x + dims.width / 2,
          y: pos.y + dims.height / 2,
          w: dims.width,
          h: dims.height
        });
      });

      nextNodes.push(
        ...buildEdgeChipNodes<DiagramNodeData>({
          edgeChipNodes: visibleTopologyNodes.filter((n) => embedParents.has(n.id) && isEdgeChipNode(n.id)),
          nodeById,
          embedParents,
          childrenByParent,
          edgesByNode,
          topNodeBoxes,
          deviceDimensions,
          nodeUIById,
          overlayKeyByNodeId,
          nodeOverlayStyle
        })
      );

      const rfNodeById = new Map<string, Node<DiagramNodeData>>();
      nextNodes.forEach((node) => rfNodeById.set(node.id, node));

      const portAnchors = new Map<string, PortAnchor>();
      const kindByPortId: Record<string, string | undefined> = {};
      const uiByPortId: Record<string, Record<string, unknown> | undefined> = {};

      const deviceCenters = new Map<string, { x: number; y: number; w: number; h: number }>();
      nextNodes.forEach((node) => {
        const dims = diagramNodeDimensions(node, deviceDimensions);
        const origin = absoluteNodeOrigin(node, rfNodeById);
        deviceCenters.set(node.id, {
          x: origin.x + dims.width / 2,
          y: origin.y + dims.height / 2,
          w: dims.width,
          h: dims.height
        });
      });

      const portsByParent = new Map<string, Array<{ portId: string; text: string }>>();
      visibleTopologyNodes.forEach((n) => {
        const parentId = visualPortParentId(n.id);
        if (!parentId) {
          return;
        }
        if (l3MembershipGraph && isL3VrfNode(n)) {
          return;
        }
        if (isEdgeChipNode(n.id)) {
          return;
        }
        kindByPortId[n.id] = n.labels?.['if.kind'];
        const portUI = nodeUIById.get(n.id);
        uiByPortId[n.id] = portUI;
        const portText =
          getString(portUI?.portLabel) ??
          n.displayName ??
          n.id;
        const list = portsByParent.get(parentId) ?? [];
        list.push({ portId: n.id, text: portText });
        portsByParent.set(parentId, list);
      });

      portsByParent.forEach((ports, parentId) => {
        const parentCenter = deviceCenters.get(parentId);
        if (!parentCenter) {
          return;
        }
        const fallback: PortSide[] = ['left', 'right', 'top', 'bottom'];
        let fallbackIdx = 0;

        ports.forEach((port) => {
          const incident = edgesByNode.get(port.portId) ?? [];

          const connects =
            incident.find((e) => e.labels?.relation === 'connects') ??
            incident.find((e) => e.labels?.relation === 'member_of') ??
            incident[0];

          if (!connects) {
            const side = fallback[fallbackIdx++ % fallback.length];
            portAnchors.set(port.portId, { portId: port.portId, text: port.text, side, x: 0, y: 0 });
            return;
          }

          const otherId = connects.fromId === port.portId ? connects.toId : connects.fromId;
          const otherTop = topAncestorFor(otherId);
          const otherCenter = deviceCenters.get(otherTop);

          if (!otherCenter || otherTop === parentId) {
            const side = fallback[fallbackIdx++ % fallback.length];
            portAnchors.set(port.portId, { portId: port.portId, text: port.text, side, x: 0, y: 0 });
            return;
          }

          const side = pickSideByBoxes(
            { x: parentCenter.x, y: parentCenter.y, w: parentCenter.w, h: parentCenter.h },
            { x: otherCenter.x, y: otherCenter.y, w: otherCenter.w, h: otherCenter.h }
          );
          portAnchors.set(port.portId, { portId: port.portId, text: port.text, side, x: 0, y: 0 });
        });

        const place = (side: PortSide) => {
          const sidePorts = ports.filter((p) => portAnchors.get(p.portId)?.side === side);
          if (sidePorts.length === 0) {
            return;
          }
          const ordered = [...sidePorts].sort((a, b) => a.text.localeCompare(b.text));
          const w = parentCenter.w;
          const h = parentCenter.h;
          const cx = w / 2;
          const cy = h / 2;
          const dotInset = isEdgeChipNode(parentId) ? 4 : 0;
          const availW = Math.max(0, w - 2 * dotInset - PORT_DOT_SIZE);
          const availH = Math.max(0, h - 2 * dotInset - PORT_DOT_SIZE);
          const pitch =
            ordered.length <= 1
              ? 0
              : side === 'top' || side === 'bottom'
                ? Math.min(PORT_PITCH, availW / Math.max(1, ordered.length - 1))
                : Math.min(PORT_PITCH, availH / Math.max(1, ordered.length - 1));

          ordered.forEach((port, idx) => {
            const offset = (idx - (ordered.length - 1) / 2) * pitch;
            let x = cx - PORT_DOT_SIZE / 2;
            let y = cy - PORT_DOT_SIZE / 2;
            if (side === 'top') {
              x = cx + offset - PORT_DOT_SIZE / 2;
              y = dotInset;
            } else if (side === 'bottom') {
              x = cx + offset - PORT_DOT_SIZE / 2;
              y = h - dotInset - PORT_DOT_SIZE;
            } else if (side === 'left') {
              x = dotInset;
              y = cy + offset - PORT_DOT_SIZE / 2;
            } else if (side === 'right') {
              x = w - dotInset - PORT_DOT_SIZE;
              y = cy + offset - PORT_DOT_SIZE / 2;
            }

            x = Math.max(dotInset, Math.min(w - dotInset - PORT_DOT_SIZE, x));
            y = Math.max(dotInset, Math.min(h - dotInset - PORT_DOT_SIZE, y));

            const anchor = portAnchors.get(port.portId);
            if (anchor) {
              anchor.x = x;
              anchor.y = y;
              anchor.slot = idx;
            }
          });
        };

        place('top');
        place('right');
        place('bottom');
        place('left');
      });

      const lagBundles = new Map<
        string,
        { parentId: string; lagId: string; lag: PortAnchor; members: PortAnchor[]; style: Record<string, unknown> }
      >();

      nonEmbedEdges.forEach((e) => {
        const relation = e.labels?.relation;
        if (relation !== 'member_of') {
          return;
        }
        const fromParent = embedParents.get(e.fromId);
        const toParent = embedParents.get(e.toId);
        if (!fromParent || !toParent || fromParent !== toParent) {
          return;
        }
        const fromKind = kindByPortId[e.fromId];
        const toKind = kindByPortId[e.toId];
        const lagId = fromKind === 'lag' ? e.fromId : toKind === 'lag' ? e.toId : null;
        const memberId = lagId === e.fromId ? e.toId : lagId === e.toId ? e.fromId : null;
        if (!lagId || !memberId) {
          return;
        }
        const lag = portAnchors.get(lagId);
        const member = portAnchors.get(memberId);
        if (!lag || !member) {
          return;
        }
        const key = `${fromParent}:${lagId}`;
        const lagUI = nodeUIById.get(lagId);
        const lagPortStyle = asRecord(lagUI?.portStyle);
        const lagStyle = asRecord(lagUI?.style);
        const lagColor =
          getString(lagPortStyle?.color) ??
          getString(lagUI?.portColor) ??
          getString(lagUI?.color) ??
          getString(lagStyle?.color) ??
          getString(lagStyle?.stroke) ??
          '#6366f1';
        const bundleStyle = { strokeDasharray: '6 4', strokeWidth: 2, stroke: lagColor };
        const existing = lagBundles.get(key);
        if (existing) {
          existing.members.push(member);
        } else {
          lagBundles.set(key, { parentId: fromParent, lagId, lag, members: [member], style: bundleStyle });
        }
      });

      const hidePortIds: Record<string, true> = {};
      lagBundles.forEach((bundle) => {
        if (bundle.members.length > 0) {
          hidePortIds[bundle.lagId] = true;
        }
      });

      const decorEdges: Edge[] = Array.from(portsByParent.entries()).map(([parentId, ports]) => {
        const placed = ports
          .map((p) => portAnchors.get(p.portId))
          .filter((p): p is PortAnchor => Boolean(p));
        return {
          id: `portdecor:${parentId}`,
          source: parentId,
          target: parentId,
          type: 'portdecor',
          data: {
            ports: placed,
            kindByPortId,
            uiByPortId,
            hidePortIds,
            overlayKey: overlayKeyByNodeId[parentId]
          } satisfies PortDecorEdgeData
        } as Edge;
      });

      const bundleItems = Array.from(lagBundles.values()).map((bundle) => {
        const memberSideCounts = new Map<PortSide, number>();
        bundle.members.forEach((m) => memberSideCounts.set(m.side, (memberSideCounts.get(m.side) ?? 0) + 1));
        const dominantSide = (['top', 'right', 'bottom', 'left'] as const).reduce((best, side) => {
          return (memberSideCounts.get(side) ?? 0) > (memberSideCounts.get(best) ?? 0) ? side : best;
        }, 'bottom' as PortSide);

        const baseLag =
          bundle.members.length === 0
            ? bundle.lag
            : ({
                ...bundle.lag,
                side: dominantSide,
                x: bundle.members.reduce((sum, m) => sum + m.x, 0) / bundle.members.length,
                y: bundle.members.reduce((sum, m) => sum + m.y, 0) / bundle.members.length
              } satisfies PortAnchor);

        return { ...bundle, dominantSide, baseLag };
      });

      const grouped = new Map<string, typeof bundleItems>();
      bundleItems.forEach((b) => {
        const key = `${b.parentId}:${b.dominantSide}`;
        const list = grouped.get(key) ?? [];
        list.push(b);
        grouped.set(key, list);
      });

      grouped.forEach((list) => {
        list.sort((a, b) => a.baseLag.text.localeCompare(b.baseLag.text));
        const parentCenter = deviceCenters.get(list[0]?.parentId ?? '');
        if (!parentCenter) {
          return;
        }
        const w = parentCenter.w;
        const h = parentCenter.h;
        list.forEach((b, idx) => {
          const shift = (idx - (list.length - 1) / 2) * LAG_BUNDLE_CUSHION;
          let x = b.baseLag.x;
          let y = b.baseLag.y;
          if (b.dominantSide === 'top' || b.dominantSide === 'bottom') {
            x = x + shift;
          } else {
            y = y + shift;
          }
          x = Math.max(0, Math.min(w - PORT_DOT_SIZE, x));
          y = Math.max(0, Math.min(h - PORT_DOT_SIZE, y));
          b.baseLag = { ...b.baseLag, x, y };
        });
      });

      const bundleEdges: Edge[] = bundleItems.map((bundle) => ({
        id: `lagbundle:${bundle.parentId}:${bundle.lagId}`,
        source: bundle.parentId,
        target: bundle.parentId,
        type: 'lagbundle',
        data: {
          lag: bundle.baseLag,
          members: bundle.members,
          style: bundle.style,
          overlayKey: overlayKeyByNodeId[bundle.parentId]
        } satisfies LagBundleEdgeData
      }));

      const lagAnchorByPortID = new Map<string, PortAnchor>();
      bundleItems.forEach((bundle) => {
        lagAnchorByPortID.set(bundle.lagId, bundle.baseLag);
      });

      const interactivePortNodes: Node<DiagramNodeData>[] = visibleTopologyNodes.flatMap((n) => {
        const parentId = visualPortParentId(n.id);
        if (!parentId || isEdgeChipNode(n.id)) {
          return [];
        }

        const kind = kindByPortId[n.id];
        const anchor = kind === 'lag' ? lagAnchorByPortID.get(n.id) ?? portAnchors.get(n.id) : portAnchors.get(n.id);
        if (!anchor) {
          return [];
        }

        const size = kind === 'lag' ? LAG_HIT_TARGET_SIZE : PORT_HIT_TARGET_SIZE;
        return [{
          id: n.id,
          parentNode: parentId,
          position: {
            x: anchor.x + PORT_DOT_SIZE / 2 - size / 2,
            y: anchor.y + PORT_DOT_SIZE / 2 - size / 2
          },
          draggable: false,
          selectable: true,
          data: {
            text: n.displayName ?? n.id,
            label: <div />,
            ui: nodeUIById.get(n.id) ?? {},
            overlayKey: overlayKeyByNodeId[n.id]
          },
          className: 'topology-v2__node--port-hit-target',
          style: {
            width: size,
            height: size,
            borderRadius: 999,
            border: '1px solid transparent',
            background: 'transparent',
            boxShadow: 'none',
            zIndex: 30
          }
        } satisfies Node<DiagramNodeData>];
      });

      const parallelSlots = new Map<string, { index: number; total: number }>();
      const topLevelEdgeGroups = new Map<string, TopologyEdge[]>();
      nonEmbedEdges.forEach((edge) => {
        if (!nodeById.has(edge.fromId) || !nodeById.has(edge.toId)) {
          return;
        }
        if (edge.labels?.relation === 'member_of') {
          return;
        }
        if ((embedParents.has(edge.fromId) || embedParents.has(edge.toId)) && !(isEdgeChipNode(edge.fromId) && isEdgeChipNode(edge.toId))) {
          return;
        }
        const key =
          edge.fromId < edge.toId ? `${edge.fromId}--${edge.toId}` : `${edge.toId}--${edge.fromId}`;
        const group = topLevelEdgeGroups.get(key) ?? [];
        group.push(edge);
        topLevelEdgeGroups.set(key, group);
      });
      topLevelEdgeGroups.forEach((group) => {
        if (group.length < 2) {
          return;
        }
        const ordered = [...group].sort((a, b) => a.id.localeCompare(b.id));
        ordered.forEach((edge, index) => {
          parallelSlots.set(edge.id, { index, total: ordered.length });
        });
      });

      const linkEdges: Edge[] = nonEmbedEdges
        .filter((e) => nodeById.has(e.fromId) && nodeById.has(e.toId))
        .flatMap((e) => {
          const ui = pickEdgeUI(e);
          const edgeUI = topologyPresentationProps(e.properties);
          const uiLabelField = typeof ui.labelField === 'string' ? ui.labelField.trim() : '';
          const uiLabelValue = uiLabelField ? e.properties?.[uiLabelField] : undefined;
          const edgeLabel =
            typeof edgeUI?.edgeLabel === 'string'
              ? edgeUI.edgeLabel.trim()
              : typeof uiLabelValue === 'string' || typeof uiLabelValue === 'number'
                ? String(uiLabelValue)
                : '';
          const runtimeEdgeLabel = runtimeLabelText(e.properties, ['edge_badge', 'edge_label']);
          const displayEdgeLabel = [edgeLabel, runtimeEdgeLabel].filter((label) => label.trim().length > 0).join(' · ');
          const style = typeof ui.style === 'string' ? ui.style : 'link';
          let edgeStyle: Record<string, unknown> =
            style === 'membership'
              ? { strokeDasharray: '6 4', strokeWidth: 2, stroke: '#6366f1' }
              : style === 'hierarchy'
                ? { strokeWidth: 1, stroke: '#94a3b8' }
                : { strokeWidth: 2, stroke: '#6366f1' };

          if (e.labels?.level === 'lag_link') {
            edgeStyle.strokeWidth = 3;
          }
          const legendStroke = typeof ui.stroke === 'string' ? ui.stroke.trim() : '';
          if (legendStroke) {
            edgeStyle.stroke = legendStroke;
          }
          const uiStroke = typeof e.properties?.['ui.stroke'] === 'string' ? (e.properties?.['ui.stroke'] as string) : '';
          if (uiStroke) {
            edgeStyle.stroke = uiStroke;
          }
          const uiWidth = typeof ui.width === 'number' ? ui.width : typeof ui.width === 'string' ? Number(ui.width) : NaN;
          if (Number.isFinite(uiWidth) && uiWidth > 0) {
            edgeStyle.strokeWidth = uiWidth;
          }
          edgeStyle = applyTopologyEdgeStrokePattern(edgeStyle, ui as Record<string, unknown>);
          const linkStateStroke = pickEdgeLinkStateStroke(e, ui as Record<string, unknown>, nodeById);
          if (linkStateStroke) {
            edgeStyle.stroke = linkStateStroke;
          }
          edgeStyle = applyEdgeOverlayStyle(edgeStyle, e);

          const relation = e.labels?.relation;
          const markerEnd =
            relation === 'depends_on'
              ? {
                  type: MarkerType.ArrowClosed,
                  color: typeof edgeStyle.stroke === 'string' ? edgeStyle.stroke : '#6366f1'
                }
              : undefined;
          const directChipLink = isEdgeChipNode(e.fromId) && isEdgeChipNode(e.toId);
          if (relation === 'member_of') {
            return [];
          }
          if (
            relation === 'connects' &&
            kindByPortId[e.fromId] === 'lag' &&
            kindByPortId[e.toId] === 'lag' &&
            normalizedString(e.labels?.family) !== 'l3' &&
            e.labels?.level !== 'lag_link'
          ) {
            return [];
          }

          const fromParent = visualPortParentId(e.fromId);
          const toParent = visualPortParentId(e.toId);
          const fromAnchor = portAnchors.get(e.fromId);
          const toAnchor = portAnchors.get(e.toId);
          if (directChipLink) {
            const parallel = parallelSlots.get(e.id);
            return [
              {
                id: e.id,
                source: e.fromId,
                target: e.toId,
                animated: false,
                type: parallel ? 'parallel' : 'straight',
                interactionWidth: 22,
                style: parallel ? undefined : (edgeStyle as CSSProperties),
                label: parallel ? undefined : displayEdgeLabel || undefined,
                labelStyle: parallel ? undefined : { fontSize: 11, fontWeight: 700, fill: '#312e81' },
                labelBgStyle: parallel ? undefined : { fill: 'rgba(238, 242, 255, 0.95)' },
                labelBgPadding: parallel ? undefined : [2, 4],
                markerEnd,
                data: parallel
                  ? ({
                      label: displayEdgeLabel || undefined,
                      style: edgeStyle,
                      offsetIndex: parallel.index,
                      offsetTotal: parallel.total,
                      overlayKey: overlayKeyByEdgeId[e.id]
                    } satisfies ParallelEdgeData)
                  : { overlayKey: overlayKeyByEdgeId[e.id] }
              } as Edge
            ];
          }
          if (fromParent && toParent && fromAnchor && toAnchor) {
            return [
              {
                id: e.id,
                source: fromParent,
                target: toParent,
                type: 'portlink',
                interactionWidth: 22,
                data: {
                  sourcePort: fromAnchor,
                  targetPort: toAnchor,
                  label: displayEdgeLabel || undefined,
                  style: edgeStyle,
                  overlayKey: overlayKeyByEdgeId[e.id]
                } satisfies PortLinkEdgeData
              } as Edge
            ];
          }
          if (fromParent && fromAnchor && !toParent) {
            return [
              {
                id: e.id,
                source: fromParent,
                target: e.toId,
                type: 'portlink',
                interactionWidth: 22,
                data: {
                  sourcePort: fromAnchor,
                  label: displayEdgeLabel || undefined,
                  style: edgeStyle,
                  overlayKey: overlayKeyByEdgeId[e.id]
                } satisfies PortLinkEdgeData
              } as Edge
            ];
          }
          if (!fromParent && toParent && toAnchor) {
            return [
              {
                id: e.id,
                source: e.fromId,
                target: toParent,
                type: 'portlink',
                interactionWidth: 22,
                data: {
                  targetPort: toAnchor,
                  label: displayEdgeLabel || undefined,
                  style: edgeStyle,
                  overlayKey: overlayKeyByEdgeId[e.id]
                } satisfies PortLinkEdgeData
              } as Edge
            ];
          }

          if (!embedParents.has(e.fromId) && !embedParents.has(e.toId)) {
            const parallel = parallelSlots.get(e.id);
            if (parallel) {
              return [
                {
                  id: e.id,
                  source: e.fromId,
                  target: e.toId,
                  animated: false,
                  type: 'parallel',
                  interactionWidth: 22,
                  markerEnd,
                  data: {
                    label: displayEdgeLabel || undefined,
                    style: edgeStyle,
                    offsetIndex: parallel.index,
                    offsetTotal: parallel.total,
                    overlayKey: overlayKeyByEdgeId[e.id]
                  } satisfies ParallelEdgeData
                } as Edge
              ];
            }
            return [
              {
                id: e.id,
                source: e.fromId,
                target: e.toId,
                animated: false,
                type: 'straight',
                interactionWidth: 22,
                style: edgeStyle as CSSProperties,
                label: displayEdgeLabel || undefined,
                labelStyle: { fontSize: 11, fontWeight: 700, fill: '#312e81' },
                labelBgStyle: { fill: 'rgba(238, 242, 255, 0.95)' },
                labelBgPadding: [2, 4],
                markerEnd,
                data: { overlayKey: overlayKeyByEdgeId[e.id] }
              }
            ];
          }
          return [];
        });

      const nextEdges: Edge[] = [...decorEdges, ...bundleEdges, ...linkEdges];

      if (cancelled || !topologyLayoutBuildIsCurrent(buildRevision, layoutRevisionRef.current)) {
        return;
      }
      setNodes([...nextNodes, ...interactivePortNodes]);
      setEdges(nextEdges);
      lastLayoutKeyRef.current = layoutScopeKey;
    };

    void build();

    return () => {
      cancelled = true;
    };
  }, [graphKey, overlayKeyByEdgeId, overlayKeyByNodeId, pickEdgeUI, pickNodeUI, topology, setEdges, setNodes]);

  return (
    <div
      ref={containerRef}
      className="topology-diagram__canvas topology-canvas"
      onMouseDown={(event) => {
        if (!hoverTarget) {
          return;
        }
        if (event.button !== 0) {
          return;
        }
        handledPointerRef.current = true;
        window.setTimeout(() => {
          handledPointerRef.current = false;
        }, 0);
        event.stopPropagation();
        event.preventDefault();
        if (event.detail >= 2) {
          if (hoverTarget.kind === 'node') {
            onNodeDoubleClick(event, hoverTarget.id);
          } else {
            onEdgeDoubleClick(event, hoverTarget.id);
          }
        } else if (hoverTarget.kind === 'node') {
          onNodeSelect(event, hoverTarget.id);
        } else {
          onEdgeSelect(event, hoverTarget.id);
        }
        setHoverTarget(null);
        setHoverPos(null);
      }}
    >
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange as OnNodesChange}
        onEdgesChange={onEdgesChange as OnEdgesChange}
        nodeDragThreshold={8}
        onNodeClick={(event, node) => {
          event.stopPropagation();
          event.preventDefault();
          if (handledPointerRef.current) {
            handledPointerRef.current = false;
            return;
          }
          onNodeSelect(event as unknown as React.MouseEvent, node.id);
          setHoverTarget(null);
          setHoverPos(null);
        }}
        onNodeDoubleClick={(event, node) => {
          event.stopPropagation();
          event.preventDefault();
          if (handledPointerRef.current) {
            handledPointerRef.current = false;
            return;
          }
          onNodeDoubleClick(event as unknown as React.MouseEvent, node.id);
          setHoverTarget(null);
          setHoverPos(null);
        }}
        onEdgeClick={(event, edge) => {
          event.stopPropagation();
          event.preventDefault();
          if (handledPointerRef.current) {
            handledPointerRef.current = false;
            return;
          }
          onEdgeSelect(event as unknown as React.MouseEvent, edge.id);
          setHoverTarget(null);
          setHoverPos(null);
        }}
        onEdgeDoubleClick={(event, edge) => {
          event.stopPropagation();
          event.preventDefault();
          if (handledPointerRef.current) {
            handledPointerRef.current = false;
            return;
          }
          onEdgeDoubleClick(event as unknown as React.MouseEvent, edge.id);
          setHoverTarget(null);
          setHoverPos(null);
        }}
        onPaneClick={(event) => {
          onPaneClick(event as unknown as React.MouseEvent);
        }}
        onNodeMouseEnter={(event, node) => {
          if (suppressNodeHoverTooltip(event.target)) {
            setHoverTarget(null);
            setHoverPos(null);
            return;
          }
          setHoverTarget({ kind: 'node', id: node.id });
          updateHoverPos(event as unknown as React.MouseEvent);
        }}
        onNodeMouseMove={(event) => {
          if (suppressNodeHoverTooltip(event.target)) {
            setHoverTarget(null);
            setHoverPos(null);
            return;
          }
          updateHoverPos(event as unknown as React.MouseEvent);
        }}
        onNodeMouseLeave={() => {
          setHoverTarget((prev) => (prev?.kind === 'node' ? null : prev));
          setHoverPos(null);
        }}
        onEdgeMouseEnter={(event, edge) => {
          if (!topologyEdgeById.has(edge.id)) {
            return;
          }
          setHoverTarget({ kind: 'edge', id: edge.id });
          updateHoverPos(event as unknown as React.MouseEvent);
        }}
        onEdgeMouseMove={(event) => {
          updateHoverPos(event as unknown as React.MouseEvent);
        }}
        onEdgeMouseLeave={() => {
          setHoverTarget((prev) => (prev?.kind === 'edge' ? null : prev));
          setHoverPos(null);
        }}
        minZoom={0.1}
        maxZoom={2.5}
        zoomOnDoubleClick={false}
        preventScrolling
        panOnScroll
        zoomOnScroll={false}
      >
        <Background gap={18} size={1} />
        <FitViewWhenReady graphKey={graphKey} nodeCount={rfNodes.length} containerRef={containerRef} />
        {showControls && <MiniMap pannable zoomable />}
        {showControls && <Controls />}
      </ReactFlow>

      {hoverContent && hoverPos && (
        <div
          className="topology-tooltip topology-tooltip--floating"
          style={{ left: hoverPos.x, top: hoverPos.y, pointerEvents: 'none' }}
        >
          <div style={{ fontWeight: 700 }}>{hoverContent.title}</div>
          <ul>
            {hoverContent.rows.length > 0 ? (
              hoverContent.rows.map((row) => (
                <li key={row.key}>
                  <span style={{ fontWeight: 600 }}>{row.label}</span>:{' '}
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{row.value}</span>
                </li>
              ))
            ) : (
              <li style={{ fontStyle: 'italic' }}>No properties</li>
            )}
            {hoverContent.truncated && <li style={{ fontStyle: 'italic' }}>...</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TopologyCanvas;
